// Route table and cross-cutting route effects (meta + page analytics).
// The studio, Stage and Time Machine are fullscreen surfaces without the shell.

import { Suspense, lazy, useEffect } from "react";
import { BrowserRouter, Route, Routes, useLocation } from "react-router-dom";
import { AppShell } from "@/views/shell/AppShell";
import { ToastHost } from "@/ui/Toast";
import { ContextMenuHost } from "@/ui/ContextMenu";
import { applyMeta } from "@/seo/meta";
import { page } from "@/analytics/service";
import { useAuth } from "@/state/auth";
import { Oscilloscope } from "@/illustrations";
import { ConsentBanner } from "@/views/company/ConsentBanner";
import { ErrorBoundary } from "@/views/shell/ErrorBoundary";

const Landing = lazy(() => import("@/views/landing/Landing"));
const Universe = lazy(() => import("@/views/universe/Universe"));
const Studio = lazy(() => import("@/views/studio/Studio"));
const Stage = lazy(() => import("@/views/stage/Stage"));
const Discover = lazy(() => import("@/views/community/Discover"));
const Biography = lazy(() => import("@/views/community/Biography"));
const Creator = lazy(() => import("@/views/community/Creator"));
const ChallengePage = lazy(() => import("@/views/community/ChallengePage"));
const Marketplace = lazy(() => import("@/views/marketplace/Marketplace"));
const Learn = lazy(() => import("@/views/learn/Learn"));
const Reflections = lazy(() => import("@/views/reflections/Reflections"));
const Wrapped = lazy(() => import("@/views/reflections/Wrapped"));
const Settings = lazy(() => import("@/views/settings/Settings"));
const CompanyPage = lazy(() => import("@/views/company/CompanyPage"));
const Status = lazy(() => import("@/views/company/Status"));
const Blog = lazy(() => import("@/views/blog/Blog"));
const BlogPost = lazy(() => import("@/views/blog/BlogPost"));
const AdminFeatured = lazy(() => import("@/views/admin/Featured"));
const AdminBlog = lazy(() => import("@/views/admin/Blog"));
const AdminDocumentation = lazy(() => import("@/views/admin/Documentation"));
const Playground = lazy(() => import("@/views/playground/Playground"));
const AuthView = lazy(() => import("@/views/auth/AuthView"));
const NotFound = lazy(() => import("@/views/shell/NotFound"));

function RouteEffects() {
  const location = useLocation();
  useEffect(() => {
    applyMeta(location.pathname);
    page(location.pathname, document.title);
  }, [location.pathname]);
  useEffect(() => {
    void useAuth.getState().refresh();
  }, []);
  return null;
}

function Loading() {
  return (
    <div style={{ display: "grid", placeItems: "center", minHeight: "60vh" }}>
      <div style={{ width: 200 }} aria-label="Loading" role="status">
        <Oscilloscope />
      </div>
    </div>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <RouteEffects />
      <ErrorBoundary>
        <Suspense fallback={<Loading />}>
          <Routes>
          <Route path="/" element={<Landing />} />
          <Route path="/studio/:draftId" element={<Studio />} />
          <Route path="/stage/:draftId" element={<Stage />} />
          <Route path="/reflections/wrapped" element={<Wrapped />} />
          <Route path="/auth" element={<AuthView />} />
          <Route element={<AppShell />}>
            <Route path="/universe" element={<Universe />} />
            <Route path="/discover" element={<Discover />} />
            <Route path="/discover/challenge/:id" element={<ChallengePage />} />
            <Route path="/session/:id" element={<Biography />} />
            <Route path="/creator/:handle" element={<Creator />} />
            <Route path="/marketplace" element={<Marketplace />} />
            <Route path="/learn" element={<Learn />} />
            <Route path="/reflections" element={<Reflections />} />
            <Route path="/settings" element={<Settings />} />
            <Route path="/company/:slug" element={<CompanyPage />} />
            <Route path="/legal/:slug" element={<CompanyPage />} />
            <Route path="/status" element={<Status />} />
            <Route path="/blog" element={<Blog />} />
            <Route path="/blog/:slug" element={<BlogPost />} />
            <Route path="/admin/featured" element={<AdminFeatured />} />
            <Route path="/admin/blog" element={<AdminBlog />} />
            <Route path="/admin/documentation" element={<AdminDocumentation />} />
            {import.meta.env.DEV && <Route path="/playground" element={<Playground />} />}
            <Route path="*" element={<NotFound />} />
          </Route>
          </Routes>
        </Suspense>
      </ErrorBoundary>
      <ToastHost />
      <ContextMenuHost />
      <ConsentBanner />
    </BrowserRouter>
  );
}
